import { useCallback } from "react";
import { toast } from "react-toastify";
import { useUpdateFacet } from "../../tars-hooks";
import type { TarsAnalyticsStore } from "../../../../stores/tars/tars-analytics-store";
import {
    currentFacetResult,
    mutateFacetUpdate,
    resolveWorkspaceEtlJson,
    syncFacetMoreToStore,
    syncFacetResultToStore,
    type WorkspaceEtlFallback,
} from "./facet-workspace-persist-helpers";
import type { FacetSnapshotForUpdate } from "./facet-workspace-update-helpers";

interface PersistFacetResultOptions {
    facet: FacetSnapshotForUpdate;
    facet_result: Record<string, unknown>;
    etl_json?: Record<string, unknown>;
    jsonDraft?: string;
    etlFallback?: WorkspaceEtlFallback;
    more?: Record<string, unknown>;
    successToast: string;
    missingServerMessage: string;
    onSuccess?: (savedFacetResult: Record<string, unknown>) => void;
    onError?: (error: unknown) => void;
}

interface PersistFacetMoreOptions {
    facet: FacetSnapshotForUpdate;
    more: Record<string, unknown>;
    etl_json?: Record<string, unknown>;
    successToast: string;
    missingServerMessage: string;
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
}

export function usePersistFacetUpdate(store: TarsAnalyticsStore, notebookId?: number) {
    const updateFacetApi = useUpdateFacet();
    const updateFacet = store.updateFacet.bind(store);
    const resolvedNotebookId =
        notebookId != null && Number.isFinite(notebookId) && notebookId > 0 ? notebookId : undefined;

    const persistFacetResult = useCallback(
        (opts: PersistFacetResultOptions): Promise<void> =>
            new Promise((resolve, reject) => {
                const etlJson =
                    opts.etl_json ??
                    resolveWorkspaceEtlJson(opts.facet, opts.jsonDraft, opts.etlFallback ?? "persist");
                const started = mutateFacetUpdate({
                    facet: opts.facet,
                    notebookId: resolvedNotebookId,
                    payloadOverrides: {
                        etl_json: etlJson,
                        facet_result: opts.facet_result,
                        more: opts.more,
                    },
                    missingServerMessage: opts.missingServerMessage,
                    mutate: updateFacetApi.mutate,
                    onSuccess: (resp) => {
                        toast.success(opts.successToast);
                        const saved = syncFacetResultToStore(
                            updateFacet,
                            opts.facet.id,
                            resp,
                            opts.facet_result
                        );
                        opts.onSuccess?.(saved);
                        resolve();
                    },
                    onError: (error) => {
                        opts.onError?.(error);
                        reject(error);
                    },
                });
                if (!started) resolve();
            }),
        [resolvedNotebookId, updateFacet, updateFacetApi]
    );

    const persistFacetMore = useCallback(
        (opts: PersistFacetMoreOptions): Promise<void> =>
            new Promise((resolve, reject) => {
                const started = mutateFacetUpdate({
                    facet: opts.facet,
                    notebookId: resolvedNotebookId,
                    payloadOverrides: {
                        etl_json: opts.etl_json ?? resolveWorkspaceEtlJson(opts.facet),
                        facet_result: currentFacetResult(opts.facet),
                        more: opts.more,
                    },
                    missingServerMessage: opts.missingServerMessage,
                    mutate: updateFacetApi.mutate,
                    onSuccess: (resp) => {
                        toast.success(opts.successToast);
                        syncFacetMoreToStore(updateFacet, opts.facet.id, resp, opts.more);
                        opts.onSuccess?.();
                        resolve();
                    },
                    onError: (error) => {
                        opts.onError?.(error);
                        reject(error);
                    },
                });
                if (!started) resolve();
            }),
        [resolvedNotebookId, updateFacet, updateFacetApi]
    );

    return {
        persistFacetResult,
        persistFacetMore,
        isPersisting: updateFacetApi.isLoading,
        updateFacetApi,
    };
}
